import {formValueSelector} from 'redux-form';
import i18next from 'i18next';

import createSelector from 'web-app/util/custom-create-selector';
import {type RootState} from 'web-app/react/redux/main-reducer';
import * as AccountSelectors from 'web-app/react/session/account/selectors';
import {type GenericFormData, GenericFieldType, type GenericFieldElement} from 'web-app/react/components/form/model';
import {hasValue} from 'web-app/util/typescript';
import {isNonEmpty} from 'web-app/util/non-empty-array';
import * as ChildPickupModalHelpers from 'web-app/react/modals-container/child-pickup-modal/helpers';
import {required} from 'web-app/react/components/form-elements/validators';

import * as Helpers from './helpers';

const childSignIn = (state: RootState) => state.modals.signInGPS;

const makeFormValueSelector = () =>
    createSelector(
        (_, props: {childId: string}) => props.childId,
        childId => {
            const formName = Helpers.childSignInFormName(childId);
            return formValueSelector(formName);
        },
    );

const makeGetCurrentPickupTime = () => {
    const formSelector = makeFormValueSelector();
    return createSelector(
        (state, props: {childId: string}) => formSelector(state, props)(state, 'pickupTime'),
        pickupTime => pickupTime,
    );
};
const makeGetStatementsAnswer = () => {
    const formSelector = makeFormValueSelector();
    return createSelector(
        (state, props: {childId: string}) => formSelector(state, props)(state, 'statements'),
        statements => statements,
    );
};
export const makeGetIsStatementsRequiredForChild = () =>
    createSelector(
        makeChildSignInById(),
        signIn => signIn && hasValue(signIn.statements) && isNonEmpty(signIn.statements),
    );

export const childSignIns = (state: RootState) => state.modals.signInGPS.childSignIns;

export const makeChildSignInById = () =>
    createSelector(
        childSignIns,
        (_, props: {childId: string}) => props.childId,
        (signIns, childId) => {
            return signIns.get(childId);
        },
    );

const makeGetSelectedPickupId = () => createSelector(makeChildSignInById(), signIn => signIn?.pickupRelationId);

export const makeGetIsPickupInfoRequiredForChild = () =>
    createSelector(makeChildSignInById(), signIn => Boolean(signIn && signIn.requirePickupInfo));

export const makeGetIsPickupRequired = () =>
    createSelector(
        AccountSelectors.getIsEmployee,
        makeGetIsPickupInfoRequiredForChild(),
        (isEmployee, isPickupInfoRequired) => !isEmployee && isPickupInfoRequired,
    );

export const makeIsChildSignedIn = () =>
    createSelector(makeChildSignInById(), signIn => Boolean(signIn && signIn.checkedIn));

export const makeGetCanSignIn = () =>
    createSelector(makePickupFulfilled(), makeStatementsAreFulfilled(), (pickupFulfilled, statementsAreFulfilled) => {
        return pickupFulfilled && statementsAreFulfilled;
    });

export const makeGetShouldShowPickupInfo = () =>
    createSelector(
        makeIsChildSignedIn(),
        makeGetIsPickupInfoRequiredForChild(),
        (isChildSignedIn, isPickupInfoRequiredForChild) => {
            return !isChildSignedIn && isPickupInfoRequiredForChild;
        },
    );

export const makeGetChildName = () => createSelector(makeChildSignInById(), signIn => signIn?.name ?? '');

export const getHasLocation = createSelector(childSignIn, signIn => signIn.hasLocation);

export const getDistance = createSelector(childSignIn, signIn => signIn.distance);

export const institutionId = createSelector(childSignIns, signIns => {
    const signIn = signIns.first();
    return signIn && signIn.institutionId;
});

export const makeSelectedStatementIds = () => {
    const formSelector = makeFormValueSelector();
    return createSelector(
        (state, props: {childId: string}) => formSelector(state, props)(state, 'statements'),
        statements => {
            if (hasValue(statements)) {
                return Object.keys(statements);
            }
            return null;
        },
    );
};

export const makePickupFulfilled = () =>
    createSelector(
        makeGetIsPickupRequired(),
        makeGetSelectedPickupId(),
        makeGetCurrentPickupTime(),
        (isPickupRequired, pickupId, pickupTime) => {
            if (isPickupRequired) {
                return hasValue(pickupId) && hasValue(pickupTime);
            }
            return true;
        },
    );

export const makeStatementsAreFulfilled = () =>
    createSelector(
        makeGetIsStatementsRequiredForChild(),
        makeChildSignInById(),
        makeGetStatementsAnswer(),
        (statementsAreRequired, signIn, statementAnswers) => {
            if (statementsAreRequired && hasValue(signIn) && hasValue(signIn.statements)) {
                return (
                    hasValue(statementAnswers) &&
                    signIn.statements.every(statement => statementAnswers[statement.statementId])
                );
            }
            return true;
        },
    );

export const makeShowStatementChecks = () =>
    createSelector(
        makeChildSignInById(),
        makeGetCurrentPickupTime(),
        makeGetIsPickupInfoRequiredForChild(),
        makeGetIsStatementsRequiredForChild(),
        (childSignIn, pickupTime, pickupRequired, statementsRequired) => {
            return statementsRequired && (hasValue(pickupTime) || !pickupRequired) && !childSignIn?.checkedIn;
        },
    );

export const makeFormData = () =>
    createSelector(
        makeChildSignInById(),
        makeGetCurrentPickupTime(),
        makeGetIsPickupInfoRequiredForChild(),
        makeGetIsStatementsRequiredForChild(),
        makeShowStatementChecks(),
        (childSignIn, pickupTime, pickupRequired, statementsRequired, showStatements): GenericFormData => {
            const {fromMinute = 0, fromHour = 0, toHour = 0, toMinute = 0} = childSignIn?.openingHours ?? {};

            const {latestPickupHour, latestPickupMinute} = ChildPickupModalHelpers.getLatestPickupTime({
                toHour,
                toMinute,
                showPickupUntilExactClosingTime: childSignIn?.showPickupUntilExactClosingTime ?? false,
            });

            /**
             * `latestAllowedPickupTime` is used as both the maximum allowed value for the time-picker
             * as well as the default value. This was introduced to resolve a bug where the default
             * time/initial value would exceed the max. To discourage this from happening both values
             * are now referenced from this variable.
             */
            const latestAllowedPickupTime = {
                hours: latestPickupHour,
                minutes: latestPickupMinute,
            };

            const statements = childSignIn?.statements;
            const statementsSection: GenericFieldElement | null =
                hasValue(statements) && isNonEmpty(statements)
                    ? {
                          fieldType: GenericFieldType.element,
                          props: {
                              noLabel: true,
                          },
                          fields: [
                              {
                                  fieldType: GenericFieldType.section,
                                  name: 'statements',
                                  // The section -> element -> checkbox is a terrible hack
                                  // because my PM pushed me to add spacing between the checkboxes
                                  // which is out of spec
                                  fields: statements.map((statement, i) => ({
                                      fieldType: GenericFieldType.element,
                                      label: i === 0 ? i18next.t('checkin.answerChecks') : null,
                                      props: {
                                          noLabel: i > 0,
                                      },
                                      fields: [
                                          {
                                              fieldType: GenericFieldType.checkbox,
                                              name: statement.statementId,
                                              value: false,
                                              text: statement.title,
                                              props: {
                                                  ['data-e2e-id']: 'sign-in-statements',
                                              },
                                          },
                                      ],
                                  })),
                              },
                          ],
                          onlyRenderIf: showStatements,
                      }
                    : null;

            return {
                fields: [
                    {
                        name: 'pickupTime',
                        fieldType: GenericFieldType.newTimepicker,
                        label: i18next.t('checkin.pickupAt'),
                        value: statementsRequired ? null : latestAllowedPickupTime,
                        validate: pickupRequired ? [required] : undefined,
                        props: {
                            min: {
                                hours: fromHour,
                                minutes: fromMinute,
                            },
                            // validMinutes: [0, 15, 30, 45],
                            // validHours,
                            max: latestAllowedPickupTime,
                            placeholder: i18next.t('checkin.expectedPickupAt'),
                            isClearable: !pickupRequired,
                        },
                        elementProps: {
                            ['data-e2e-id']: 'sign-in-pickup-time',
                        },
                        onlyRenderIf: !childSignIn?.checkedIn,
                    },
                    ...(hasValue(statementsSection) ? [statementsSection] : []),
                ],
            };
        },
    );
